import { useEffect, useState } from 'react';

export interface FetchSucceededResult<D> {
  readonly type: 'succeeded';
  readonly data: D;
}

export interface FetchPendingResult {
  readonly type: 'pending';
}

export interface FetchFailedResult {
  readonly type: 'failed';
  readonly statusCode?: number;
}

export interface FetchInProgressResult {
  readonly type: 'in-progress';
}

export type FetchResult<D> =
  | FetchSucceededResult<D>
  | FetchFailedResult
  | FetchInProgressResult
  | FetchPendingResult;

const useFetch = <D>(url: string, options?: RequestInit): FetchResult<D> => {
  const [fetchResult, setFetchResult] = useState<FetchResult<D>>({ type: 'in-progress' });
  const [prevUrl, setPrevUrl] = useState(url);
  const [prevOptions, setPrevOptions] = useState(options);

  if (url !== prevUrl || options !== prevOptions) {
    setPrevUrl(url);
    setPrevOptions(options);
    setFetchResult({ type: 'in-progress' });
  }

  useEffect(() => {
    const abortController = new AbortController();

    fetch(url, {
      signal: abortController.signal,
      ...(options ?? {})
    })
      .then((response) => {
        let promise: Promise<D>;
        if (response.ok) {
          promise = response.json();
        } else {
          promise = Promise.reject(response);
        }
        return promise;
      })
      .then((data) => {
        setFetchResult({ type: 'succeeded', data });
      })
      .catch((e) => {
        if (e?.name !== 'AbortError') {
          let statusCode: number | undefined = e?.status;
          if (typeof statusCode === 'number') {
            statusCode = e.status;
          }

          setFetchResult({ type: 'failed', statusCode });
        }
      });

    return (): void => {
      abortController.abort();
    };
  }, [options, url]);

  return fetchResult;
};

export default useFetch;
